import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';

import { ModalHeader } from 'shared/ModalHeader';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { actionLabels } from 'constants/automation';
import { getActionDisplayText } from 'modules/automation/utils/getActionDisplayText';
import useStoppedInstance from 'modules/automation/hooks/useStoppedInstance';

const getActionLabelFromConstants = (action, actionCode) => {
  return actionLabels[actionCode] || action?.action_label || 'Unknown Action';
};

const RunStoppedAutomationModal = ({ onHide, instanceData }) => {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [isRunning, setIsRunning] = useState(false);
  const { addToast } = useToast();
  const { automationData, restartAutomationInstance } = useStoppedInstance();

  const executionSequence = automationData?.workflow?.execution_sequence || [];

  const handleRunSelected = async () => {
    if (selectedIndex === null) {
      return;
    }

    try {
      setIsRunning(true);

      const payload = {
        automation_pk: automationData?.pk,
        instance_id: instanceData?.instance_id,
        index_to_start: selectedIndex,
      };

      const response = await restartAutomationInstance(payload);

      if (response?.data) {
        addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.AUTOMATION_RUN_SUCCESS);
        onHide();
      }
    } catch (error) {
      console.log('error', error);
      addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.ERROR, toastConstant.message.AUTOMATION_RUN_FAILED);
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Dialog header={ModalHeader} visible onHide={onHide} style={{ width: '50vw' }} modal closable={false}>
      <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
        <div>
          <div className="modal-heading F-size16 text-bold">Run Pending Automations</div>
          <div className="text-muted F-size12 mt-1">Select which action to start from - all subsequent actions will be executed</div>
        </div>
      </div>
      <div className="p-fluid">
        <div className="mb-4">
          <div className="pt-1 overflow-y-auto pe-2" style={{ maxHeight: 300 }}>
            {executionSequence?.map((action, index) => {
              const isSelected = selectedIndex !== null && index >= selectedIndex;
              const isExcluded = selectedIndex !== null && index < selectedIndex;

              const getTagStyle = () => {
                if (selectedIndex === null) return {};
                if (isSelected) {
                  return {
                    backgroundColor: '#d4edda',
                    color: '#155724',
                    padding: '4px 12px',
                    borderRadius: '4px',
                    fontWeight: '500',
                  };
                }
                if (isExcluded) {
                  return {
                    backgroundColor: 'var(--black-100)',
                    color: 'var(--black-700)',
                    padding: '4px 12px',
                    borderRadius: '6px',
                    fontWeight: '500',
                  };
                }
                return {};
              };

              return (
                <div
                  key={index}
                  className="mb-3 py-2 px-3 inner-card d-flex justify-content-between align-items-center"
                  style={{
                    backgroundColor: isSelected ? 'var(--status-completed-bg)' : isExcluded ? 'var(--white)' : '',
                    border: isSelected ? '1px solid var(--status-completed-border)' : isExcluded ? '1px solid var(--black-100)' : '',
                  }}
                >
                  <div className="form-check d-flex align-items-start ps-0 pe-2">
                    <Checkbox
                      inputId={`action-${index}`}
                      checked={selectedIndex !== null && selectedIndex <= index}
                      onChange={() => (index === selectedIndex ? setSelectedIndex(null) : setSelectedIndex(index))}
                      className="me-3 mt-1"
                    />
                    <label htmlFor={`action-${index}`} className={`form-check-label flex-grow-1 cursor-pointer`}>
                      <b className={`black-700`}>{getActionLabelFromConstants(action, action?.action_code)}</b>
                      <div className="text-muted small">{getActionDisplayText(action)}</div>
                    </label>
                  </div>
                  <div className="small" style={getTagStyle()}>
                    {selectedIndex === null
                      ? ''
                      : index === selectedIndex
                      ? 'Run from Here'
                      : index < selectedIndex
                      ? 'Excluded'
                      : 'To Run'}
                  </div>
                </div>
              );
            })}

            {executionSequence?.length === 0 && <div className="text-center text-muted py-4">No actions available for this automation</div>}
          </div>
        </div>
      </div>
      <div className="footer-btn">
        <div className="col-12 d-flex justify-content-end">
          <Button label="Cancel" className="p-button-secondary outline" onClick={onHide} />
          <Button
            label={isRunning ? 'Running...' : `Run`}
            className="p-button-secondary ms-2"
            disabled={selectedIndex === null || isRunning}
            onClick={handleRunSelected}
            loading={isRunning}
          />
        </div>
      </div>
    </Dialog>
  );
};

export default RunStoppedAutomationModal;
